import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
    Button,
    Typography,
    Container,
    Paper,
    CircularProgress,
    Box,
    makeStyles,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    LinearProgress
} from '@material-ui/core';
import GetAppRoundedIcon from '@material-ui/icons/GetAppRounded';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import LastPageIcon from '@material-ui/icons/LastPage';
import * as XLSX from 'xlsx';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Papa from 'papaparse';
import {encryptData} from '../../../utils/CryptoHandler';
import { useWebSocket } from '../../../contextProvider/WebSocketProvider';

// Límite de registros por archivo soportado por el backend
const MAX_RECORDS = 5000;

const useStyles = makeStyles((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(4),
        marginTop: theme.spacing(4),
        backgroundColor: "#F7F7F7",
        borderRadius: 15
    },
    rootButton: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(4),
        marginTop: theme.spacing(4),
        backgroundColor: "transparent",
    },
    input: {
        display: 'none',
    },
    button: {
        fontSize: '1.3rem',
        width: '100%',
        backgroundColor: 'transparent',
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        '&:hover': {
            backgroundColor: 'transparent',
        },
        '&:focus': {
            backgroundColor: 'transparent',
        }
    },
    buttonSend: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    buttonCancel: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        marginLeft: theme.spacing(2),
        border: "1px red solid",
        color: "red",
    },
    fileName: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    statusMessage: {
        marginTop: theme.spacing(2),
        textAlign: 'left',
        paddingLeft: theme.spacing(2),
    },
    tableContainer: {
        marginTop: theme.spacing(2),
        overflow: 'auto',
        maxHeight: 'calc(100vh - 400px)',
        boxShadow: '0 0px 0px rgba(0, 0, 0, 0.1)',
    },
    table: {
        minWidth: 650,
    },
    tableHeader: {
        backgroundColor: '#f5f5f5',
        fontWeight: 'bold',
        position: 'sticky',
        top: 0,
        zIndex: 1
    },
    restartFileButton: {
        border: "1px red solid",
        color: "red",
        marginLeft: 10
    },
    previewTitle: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(1),
        fontWeight: 'bold'
    },
    confirmSection: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: '#f8f8f8',
        borderRadius: 8
    },
    pagination: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1, 2),
        width: '100%',
        borderTop: '1px solid #e0e0e0'
    },
    paginationInfo: {
        display: 'flex',
        alignItems: 'center'
    },
    rowsPerPageSelect: {
        margin: theme.spacing(0, 1),
        padding: theme.spacing(0.5),
        borderRadius: 4,
        border: '1px solid #ccc'
    },
    errorText: {
        color: '#d32f2f',
        fontWeight: 'bold',
        whiteSpace: 'pre-line',
    },
    successText: {
        color: '#2e7d32',
        fontWeight: 'bold',
        whiteSpace: 'pre-line',
    }
}));

// Componente para la paginación personalizada
function TablePaginationActions(props) {
    const classes = useStyles();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box className={classes.pagination}>
            <Box className={classes.paginationInfo}>
                <Typography variant="body2">
                    Filas por página:
                </Typography>
                <select
                    className={classes.rowsPerPageSelect}
                    value={rowsPerPage}
                    onChange={(e) => props.onRowsPerPageChange(e)}
                >
                    {[5, 10, 25, 50].map((option) => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </select>
            </Box>

            <Box>
                <IconButton
                    onClick={handleFirstPageButtonClick}
                    disabled={page === 0}
                    aria-label="primera página"
                >
                    <FirstPageIcon />
                </IconButton>
                <IconButton
                    onClick={handleBackButtonClick}
                    disabled={page === 0}
                    aria-label="página anterior"
                >
                    <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                    onClick={handleNextButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    aria-label="página siguiente"
                >
                    <KeyboardArrowRight />
                </IconButton>
                <IconButton
                    onClick={handleLastPageButtonClick}
                    disabled={page >= Math.ceil(count / rowsPerPage) - 1}
                    aria-label="última página"
                >
                    <LastPageIcon />
                </IconButton>
            </Box>
        </Box>
    );
}

function DialogMessage(props) {
    const { open, onClose, typeMessage, handleFileRestart, message, setActiveStep, setTipo, setExpiredToken, setCampaña } = props;
    useEffect(() => {
        
    }, [open]);
    const handleClose = () => {
        onClose();
        handleFileRestart();
        setActiveStep(0)
        setTipo('')
        setExpiredToken('1d')
        setCampaña('')
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >

            <DialogTitle id="alert-dialog-title">{(typeMessage && typeMessage === "success") ? "Información" : "Error"}</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description" style={{ whiteSpace: 'pre-line' }}>
                    {message}
                </DialogContentText>
            </DialogContent>
            <DialogActions>

                <Button onClick={handleClose} color="primary" autoFocus>
                    Aceptar
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function MasiveLinks(props) {
    const classes = useStyles();
    const { token, nombre_usuario, expired, campaña, id_commerce, setTipo, setActiveStep, setExpiredToken, setCampaña } = props;
    const fileInputRef = useRef(null);
    const { socket } = useWebSocket();

    const [selectedFile, setSelectedFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('');
    const [fileData, setFileData] = useState([]);
    const [showPreview, setShowPreview] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    // Progreso del envío de correos reportado por Socket.IO: { procesados, enviados, total }
    const [sendProgress, setSendProgress] = useState(null);


    // Estado para paginación
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            const file = event.target.files[0];
            setSelectedFile(file);
            setMessage('');
            setMessageType('');
            setPage(0); // Resetear a la primera página
            processFileForPreview(file);
        } else {
            setSelectedFile(null);
            setFileData([]);
            setShowPreview(false);
        }
    };

    // const processFileForPreview = (file) => {
    //     const reader = new FileReader();
    //     const fileName = file.name;
    //     const isCSV = fileName.toLowerCase().endsWith('.csv');

    //     reader.onload = (e) => {
    //         try {
    //             // Definir los campos requeridos
    //             const requiredFields = [
    //                 'CI',
    //                 'Nombre del cliente',
    //                 'Correo Electrónico',
    //                 'Numero de Telefono',
    //                 'Monto',
    //                 'Motivo de cobro'
    //             ];

    //             // Función para normalizar nombres de campos
    //             const normalizeField = (field) => {
    //                 return field
    //                     .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Quitar acentos
    //                     .toLowerCase()
    //                     .replace(/\s+/g, ' ') // Reemplazar múltiples espacios por uno solo
    //                     .replace(/[^\w\s]/gi, '') // Eliminar caracteres especiales
    //                     .trim();
    //             };

    //             // Función para validar CI
    //             const validateCI = (ci) => {
    //                 if (!ci) return "El campo CI está vacío";

    //                 const ciStr = ci.toString().trim();
    //                 const firstChar = ciStr.charAt(0).toUpperCase();
    //                 const digits = ciStr.substring(1).replace(/[^0-9]/g, '');

    //                 // Validar letra inicial
    //                 if (!['V', 'J', 'E'].includes(firstChar)) {
    //                     return "Error en formato de identificación: debe comenzar por V, J o E";
    //                 }

    //                 // Validar según tipo de CI
    //                 if (firstChar === 'V' || firstChar === 'E' || firstChar === 'v' || firstChar === 'e') {
    //                     if (digits.length < 6 || digits.length > 8) {
    //                         return "Error en formato de identificación: debe tener entre 6 a 8 dígitos";
    //                     }
    //                 } else if (firstChar === 'J' || firstChar === 'j') {
    //                     if (digits.length !== 9) {
    //                         return "Error en formato de identificación: el número debe tener exactamente 9 dígitos";
    //                     }
    //                 }

    //                 return null; // Válido
    //             };

    //             // Función para validar correo
    //             const validateEmail = (email) => {
    //                 if (!email) return "El campo Correo Electrónico está vacío";

    //                 const emailStr = email.toString().trim();
    //                 const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    //                 if (!emailRegex.test(emailStr)) {
    //                     return "Formato de correo inválido";
    //                 }
    //                 return null;
    //             };

    //             // Función para validar monto
    //             const validateMonto = (monto) => {
    //                 if (!monto) return "El campo Monto está vacío";

    //                 const montoStr = monto.toString().trim();
    //                 if (!/^\d+(\.\d{2})?$/.test(montoStr)) {
    //                     return "Formato inválido. Debe ser un número entero (ej. 1000) o con dos decimales (ej. 1.00)";
    //                 }
    //                 return null;
    //             };

    //             // Función para validar teléfono
    //             const validatePhone = (phone) => {
    //                 if (!phone) return "El campo Numero de Telefono está vacío";

    //                 const phoneStr = phone.toString().replace(/\D/g, ''); // Elimina todo lo que no sea dígito
    //                 const validPrefixes = ['424', '414', '416', '426', '412', '422'];

    //                 // Validar longitud
    //                 if (phoneStr.length !== 10) {
    //                     return "El número de teléfono debe tener 10 dígitos";
    //                 }

    //                 // Validar prefijos
    //                 const prefix = phoneStr.substring(0, 3);
    //                 if (!validPrefixes.includes(prefix)) {
    //                     return "El número debe comenzar con 424, 414, 416, 426, 412 o 422";
    //                 }

    //                 return null; // Válido
    //             };

    //             // Función principal de validación (MODIFICADA PARA LISTA CON PUNTOS)
    //             const validateRecords = (records, fieldMap) => {
    //                 const errors = [];
    //                 const ciMap = new Map();

    //                 records.forEach((record, index) => {
    //                     // Obtener valores usando fieldMap
    //                     const ci = record[fieldMap['ci']];
    //                     const email = record[fieldMap['correo electronico']];
    //                     const nombre = record[fieldMap['nombre del cliente']];
    //                     const phone = record[fieldMap['numero de telefono']];
    //                     const monto = record[fieldMap['monto']];
    //                     const motivo = record[fieldMap['motivo de cobro']];

    //                     // Validar CI
    //                     const ciError = validateCI(ci);
    //                     if (ciError) {
    //                         errors.push(`• Fila ${index + 2}: ${ciError}`);
    //                     } else {
    //                         // Verificar duplicados
    //                         const ciNormalized = ci.toString().trim().toUpperCase();
    //                         if (ciMap.has(ciNormalized)) {
    //                             errors.push(`• Fila ${index + 2}: CI duplicado`);
    //                         } else {
    //                             ciMap.set(ciNormalized, true);
    //                         }
    //                     }

    //                     // Validar teléfono
    //                     const phoneError = validatePhone(phone);
    //                     if (phoneError) {
    //                         errors.push(`• Fila ${index + 2}: ${phoneError}`);
    //                     }

    //                     // Validar email
    //                     const emailError = validateEmail(email);
    //                     if (emailError) {
    //                         errors.push(`• Fila ${index + 2}: ${emailError}`);
    //                     };

    //                     // Validar nombre
    //                     if (!nombre || nombre.toString().trim() === "") {
    //                         errors.push(`• Fila ${index + 2}: El campo "Nombre del cliente" está vacío`);
    //                     }

    //                     // Validar monto
    //                     const montoError = validateMonto(monto);
    //                     if (montoError) {
    //                         errors.push(`• Fila ${index + 2}: ${montoError}`);
    //                     }

    //                     // Validar motivo
    //                     if (!motivo || motivo.toString().trim() === "") {
    //                         errors.push(`• Fila ${index + 2}: El campo "Motivo de cobro" está vacío`);
    //                     }
    //                 });

    //                 return errors;
    //             };

    //             if (isCSV) {
    //                 // Procesamiento para archivos CSV
    //                 const csvString = e.target.result;
    //                 const cleanCSV = csvString.charCodeAt(0) === 0xFEFF ? csvString.substring(1) : csvString;

    //                 Papa.parse(cleanCSV, {
    //                     header: true,
    //                     skipEmptyLines: true,
    //                     dynamicTyping: true,
    //                     complete: (result) => {
    //                         const fileFields = result.meta.fields || [];
    //                         const normalizedFileFields = fileFields.map(normalizeField);
    //                         const normalizedRequiredFields = requiredFields.map(normalizeField);

    //                         // Crear fieldMap para acceso a campos
    //                         const fieldMap = {};
    //                         fileFields.forEach(originalField => {
    //                             const normalized = normalizeField(originalField);
    //                             fieldMap[normalized] = originalField;
    //                         });

    //                         // Validar campos
    //                         const hasAllRequiredFields = normalizedRequiredFields.every(field =>
    //                             normalizedFileFields.includes(field)
    //                         );
    //                         const hasExtraFields = normalizedFileFields.some(field =>
    //                             !normalizedRequiredFields.includes(field)
    //                         );

    //                         if (!hasAllRequiredFields || hasExtraFields) {
    //                             setFileData([]);
    //                             setShowPreview(false);
    //                             setMessage('Formato inválido: el archivo debe contener exactamente estos campos: CI, Nombre del cliente, Correo Electrónico, Numero de Telefono, Monto, Motivo de cobro.');
    //                             setMessageType('error');
    //                             return;
    //                         }

    //                         // Validar datos (AHORA CON FORMATO DE LISTA)
    //                         const validationErrors = validateRecords(result.data, fieldMap);
    //                         if (validationErrors.length > 0) {
    //                             setFileData([]);
    //                             setShowPreview(false);
    //                             setMessage(validationErrors.join('\n'));
    //                             setMessageType('error');
    //                             return;
    //                         }

    //                         // Continuar si no hay errores
    //                         if (result.data.length < 2) {
    //                             setFileData([]);
    //                             setShowPreview(false);
    //                             setMessage('El archivo debe contener al menos 2 registros.');
    //                             setMessageType('error');
    //                         } else {
    //                             setFileData(result.data);
    //                             setShowPreview(true);
    //                         }
    //                     },
    //                     error: (error) => {
    //                         console.error('Error parsing CSV:', error);
    //                         setMessage('Error al procesar el archivo CSV: ' + error.message);
    //                         setMessageType('error');
    //                         setShowPreview(false);
    //                     }
    //                 });
    //             } else {
    //                 // Procesamiento para archivos Excel
    //                 const data = new Uint8Array(e.target.result);
    //                 const workbook = XLSX.read(data, { type: 'array' });
    //                 const firstSheetName = workbook.SheetNames[0];
    //                 const worksheet = workbook.Sheets[firstSheetName];
    //                 const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    //                 // Filtrar filas vacías
    //                 const nonEmptyRows = jsonData.filter(row =>
    //                     Array.isArray(row) && row.some(cell =>
    //                         cell !== null && cell !== undefined && cell.toString().trim() !== ''
    //                     )
    //                 );

    //                 if (nonEmptyRows.length === 0) {
    //                     setFileData([]);
    //                     setShowPreview(false);
    //                     setMessage('El archivo Excel no contiene datos válidos.');
    //                     setMessageType('error');
    //                     return;
    //                 }

    //                 // Obtener encabezados
    //                 const headers = nonEmptyRows[0].map(header =>
    //                     header !== null && header !== undefined ? header.toString().trim() : ''
    //                 );

    //                 // Crear fieldMap
    //                 const fieldMap = {};
    //                 headers.forEach(originalField => {
    //                     if (originalField) {
    //                         const normalized = normalizeField(originalField);
    //                         fieldMap[normalized] = originalField;
    //                     }
    //                 });

    //                 // Validar campos
    //                 const normalizedFileHeaders = headers.map(normalizeField).filter(h => h);
    //                 const normalizedRequiredFields = requiredFields.map(normalizeField);

    //                 const hasAllRequiredFields = normalizedRequiredFields.every(field =>
    //                     normalizedFileHeaders.includes(field)
    //                 );
    //                 const hasExtraFields = normalizedFileHeaders.some(field =>
    //                     !normalizedRequiredFields.includes(field)
    //                 );

    //                 if (!hasAllRequiredFields || hasExtraFields) {
    //                     setFileData([]);
    //                     setShowPreview(false);
    //                     setMessage('Formato inválido: el archivo debe contener exactamente estos campos: CI, Nombre del cliente, Correo Electrónico, Numero de Telefono, Monto, Motivo de cobro.');
    //                     setMessageType('error');
    //                     return;
    //                 }

    //                 // Procesar datos
    //                 const formattedData = nonEmptyRows.slice(1)
    //                     .filter(row =>
    //                         Array.isArray(row) && row.some((cell, i) =>
    //                             i < headers.length && headers[i] && cell !== null && cell !== undefined && cell.toString().trim() !== ''
    //                         )
    //                     )
    //                     .map(row => {
    //                         const obj = {};
    //                         headers.forEach((header, i) => {
    //                             if (header && i < row.length) {
    //                                 obj[header] = row[i] !== null && row[i] !== undefined
    //                                     ? row[i].toString().trim()
    //                                     : '';
    //                             }
    //                         });
    //                         return obj;
    //                     });

    //                 // Validar datos (AHORA CON FORMATO DE LISTA)
    //                 const validationErrors = validateRecords(formattedData, fieldMap);
    //                 if (validationErrors.length > 0) {
    //                     setFileData([]);
    //                     setShowPreview(false);
    //                     setMessage(validationErrors.join('\n'));
    //                     setMessageType('error');
    //                     return;
    //                 }

    //                 // Continuar si no hay errores
    //                 if (formattedData.length < 2) {
    //                     setFileData([]);
    //                     setShowPreview(false);
    //                     setMessage('El archivo debe contener al menos 2 registros.');
    //                     setMessageType('error');
    //                 } else {
    //                     setFileData(formattedData);
    //                     setShowPreview(true);
    //                 }
    //             }
    //         } catch (error) {
    //             console.error('Error al procesar el archivo:', error);
    //             setMessage(`Error: ${error.message || 'Formato de archivo no válido'}`);
    //             setMessageType('error');
    //             setShowPreview(false);
    //         }
    //     };

    //     reader.onerror = () => {
    //         setMessage('Error al leer el archivo.');
    //         setMessageType('error');
    //     };

    //     if (isCSV) {
    //         reader.readAsText(file, 'UTF-8');
    //     } else {
    //         reader.readAsArrayBuffer(file);
    //     }
    // };



    const processFileForPreview = (file) => {
        const reader = new FileReader();
        const fileName = file.name;
        const isCSV = fileName.toLowerCase().endsWith('.csv');

        reader.onload = (e) => {
            try {
                // Definir los campos requeridos
                const requiredFields = [
                    'CI',
                    'Nombre del cliente',
                    'Correo Electrónico',
                    'Numero de Telefono',
                    'Monto',
                    'Motivo de cobro'
                ];

                // Función para normalizar nombres de campos
                const normalizeField = (field) => {
                    return field
                        .normalize('NFD').replace(/[\u0300-\u036f]/g, '') // Quitar acentos
                        .toLowerCase()
                        .replace(/\s+/g, ' ') // Reemplazar múltiples espacios por uno solo
                        .replace(/[^\w\s]/gi, '') // Eliminar caracteres especiales
                        .trim();
                };

                // Función para validar CI
                const validateCI = (ci) => {
                    if (!ci) return "El campo CI está vacío";

                    const ciStr = ci.toString().trim();
                    const firstChar = ciStr.charAt(0).toUpperCase();
                    const digits = ciStr.substring(1).replace(/[^0-9]/g, '');

                    // Validar letra inicial
                    if (!['V', 'J', 'E'].includes(firstChar)) {
                        return "Error en formato de identificación: debe comenzar por V, J o E";
                    }

                    // Validar según tipo de CI
                    if (firstChar === 'V' || firstChar === 'E' || firstChar === 'v' || firstChar === 'e') {
                        if (digits.length < 6 || digits.length > 8) {
                            return "Error en formato de identificación: debe tener entre 6 a 8 dígitos";
                        }
                    } else if (firstChar === 'J' || firstChar === 'j') {
                        if (digits.length !== 9) {
                            return "Error en formato de identificación: el número debe tener exactamente 9 dígitos";
                        }
                    }

                    return null; // Válido
                };

                // Función para validar correo
                const validateEmail = (email) => {
                    if (!email) return "El campo Correo Electrónico está vacío";

                    const emailStr = email.toString().trim();
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

                    if (!emailRegex.test(emailStr)) {
                        return "Formato de correo inválido";
                    }
                    return null;
                };

                // Función para validar monto (ACTUALIZADA)
                const validateMonto = (monto) => {
                    if (!monto) return "El campo Monto está vacío";

                    const montoStr = monto.toString().trim();

                    // Permitir: entero (1), un decimal (1.5), o dos decimales (1.50)
                    if (!/^\d+(\.\d{1,2})?$/.test(montoStr)) {
                        return "Formato inválido. Debe ser un número entero (ej. 1000), con un decimal (ej. 1.5) o con dos decimales (ej. 1.50)";
                    }

                    // Convertir a número y validar que sea positivo
                    const valor = parseFloat(montoStr);
                    if (valor <= 0) {
                        return "El monto debe ser mayor a cero";
                    }

                    return null;
                };

                // Función para validar teléfono
                const validatePhone = (phone) => {
                    if (!phone) return "El campo Numero de Telefono está vacío";

                    const phoneStr = phone.toString().replace(/\D/g, ''); // Elimina todo lo que no sea dígito
                    const validPrefixes = ['424', '414', '416', '426', '412', '422'];

                    // Validar longitud
                    if (phoneStr.length !== 10) {
                        return "El número de teléfono debe tener 10 dígitos";
                    }

                    // Validar prefijos
                    const prefix = phoneStr.substring(0, 3);
                    if (!validPrefixes.includes(prefix)) {
                        return "El número debe comenzar con 424, 414, 416, 426, 412 o 422";
                    }

                    return null; // Válido
                };

                // Función principal de validación
                const validateRecords = (records, fieldMap) => {
                    const errors = [];
                    const ciMap = new Map();

                    records.forEach((record, index) => {
                        // Obtener valores usando fieldMap
                        const ci = record[fieldMap['ci']];
                        const email = record[fieldMap['correo electronico']];
                        const nombre = record[fieldMap['nombre del cliente']];
                        const phone = record[fieldMap['numero de telefono']];
                        const monto = record[fieldMap['monto']];
                        const motivo = record[fieldMap['motivo de cobro']];

                        // Validar CI
                        const ciError = validateCI(ci);
                        if (ciError) {
                            errors.push(`• Fila ${index + 2}: ${ciError}`);
                        } else {
                            // Verificar duplicados
                            const ciNormalized = ci.toString().trim().toUpperCase();
                            if (ciMap.has(ciNormalized)) {
                                errors.push(`• Fila ${index + 2}: CI duplicado`);
                            } else {
                                ciMap.set(ciNormalized, true);
                            }
                        }

                        // Validar teléfono
                        // const phoneError = validatePhone(phone);
                        // if (phoneError) {
                        //     errors.push(`• Fila ${index + 2}: ${phoneError}`);
                        // }

                        // Validar email
                        const emailError = validateEmail(email);
                        if (emailError) {
                            errors.push(`• Fila ${index + 2}: ${emailError}`);
                        };

                        // Validar nombre
                        if (!nombre || nombre.toString().trim() === "") {
                            errors.push(`• Fila ${index + 2}: El campo "Nombre del cliente" está vacío`);
                        }

                        // Validar monto
                        const montoError = validateMonto(monto);
                        if (montoError) {
                            errors.push(`• Fila ${index + 2}: ${montoError}`);
                        }

                        // Validar motivo
                        if (!motivo || motivo.toString().trim() === "") {
                            errors.push(`• Fila ${index + 2}: El campo "Motivo de cobro" está vacío`);
                        }
                    });

                    return errors;
                };

                if (isCSV) {
                    // Procesamiento para archivos CSV
                    const csvString = e.target.result;
                    const cleanCSV = csvString.charCodeAt(0) === 0xFEFF ? csvString.substring(1) : csvString;

                    Papa.parse(cleanCSV, {
                        header: true,
                        skipEmptyLines: true,
                        dynamicTyping: true,
                        complete: (result) => {
                            const fileFields = result.meta.fields || [];
                            const normalizedFileFields = fileFields.map(normalizeField);
                            const normalizedRequiredFields = requiredFields.map(normalizeField);

                            // Crear fieldMap para acceso a campos
                            const fieldMap = {};
                            fileFields.forEach(originalField => {
                                const normalized = normalizeField(originalField);
                                fieldMap[normalized] = originalField;
                            });

                            // Validar campos
                            const hasAllRequiredFields = normalizedRequiredFields.every(field =>
                                normalizedFileFields.includes(field)
                            );
                            const hasExtraFields = normalizedFileFields.some(field =>
                                !normalizedRequiredFields.includes(field)
                            );

                            if (!hasAllRequiredFields || hasExtraFields) {
                                setFileData([]);
                                setShowPreview(false);
                                setMessage('Formato inválido: el archivo debe contener exactamente estos campos: CI, Nombre del cliente, Correo Electrónico, Numero de Telefono, Monto, Motivo de cobro.');
                                setMessageType('error');
                                return;
                            }

                            // Validar datos
                            const validationErrors = validateRecords(result.data, fieldMap);
                            if (validationErrors.length > 0) {
                                setFileData([]);
                                setShowPreview(false);
                                setMessage(validationErrors.join('\n'));
                                setMessageType('error');
                                return;
                            }

                            // Continuar si no hay errores
                            if (result.data.length < 2) {
                                setFileData([]);
                                setShowPreview(false);
                                setMessage('El archivo debe contener al menos 2 registros.');
                                setMessageType('error');
                            } else if (result.data.length > MAX_RECORDS) {
                                setFileData([]);
                                setShowPreview(false);
                                setMessage(`El archivo supera el límite de ${MAX_RECORDS} registros (contiene ${result.data.length}).`);
                                setMessageType('error');
                            } else {
                                setFileData(result.data);
                                setShowPreview(true);
                            }
                        },
                        error: (error) => {
                            console.error('Error parsing CSV:', error);
                            setMessage('Error al procesar el archivo CSV: ' + error.message);
                            setMessageType('error');
                            setShowPreview(false);
                        }
                    });
                } else {
                    // Procesamiento para archivos Excel
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    // Filtrar filas vacías
                    const nonEmptyRows = jsonData.filter(row =>
                        Array.isArray(row) && row.some(cell =>
                            cell !== null && cell !== undefined && cell.toString().trim() !== ''
                        )
                    );

                    if (nonEmptyRows.length === 0) {
                        setFileData([]);
                        setShowPreview(false);
                        setMessage('El archivo Excel no contiene datos válidos.');
                        setMessageType('error');
                        return;
                    }

                    // Obtener encabezados
                    const headers = nonEmptyRows[0].map(header =>
                        header !== null && header !== undefined ? header.toString().trim() : ''
                    );

                    // Crear fieldMap
                    const fieldMap = {};
                    headers.forEach(originalField => {
                        if (originalField) {
                            const normalized = normalizeField(originalField);
                            fieldMap[normalized] = originalField;
                        }
                    });

                    // Validar campos
                    const normalizedFileHeaders = headers.map(normalizeField).filter(h => h);
                    const normalizedRequiredFields = requiredFields.map(normalizeField);

                    const hasAllRequiredFields = normalizedRequiredFields.every(field =>
                        normalizedFileHeaders.includes(field)
                    );
                    const hasExtraFields = normalizedFileHeaders.some(field =>
                        !normalizedRequiredFields.includes(field)
                    );

                    if (!hasAllRequiredFields || hasExtraFields) {
                        setFileData([]);
                        setShowPreview(false);
                        setMessage('Formato inválido: el archivo debe contener exactamente estos campos: CI, Nombre del cliente, Correo Electrónico, Numero de Telefono, Monto, Motivo de cobro.');
                        setMessageType('error');
                        return;
                    }

                    // Procesar datos
                    const formattedData = nonEmptyRows.slice(1)
                        .filter(row =>
                            Array.isArray(row) && row.some((cell, i) =>
                                i < headers.length && headers[i] && cell !== null && cell !== undefined && cell.toString().trim() !== ''
                            )
                        )
                        .map(row => {
                            const obj = {};
                            headers.forEach((header, i) => {
                                if (header && i < row.length) {
                                    obj[header] = row[i] !== null && row[i] !== undefined
                                        ? row[i].toString().trim()
                                        : '';
                                }
                            });
                            return obj;
                        });

                    // Validar datos
                    const validationErrors = validateRecords(formattedData, fieldMap);
                    if (validationErrors.length > 0) {
                        setFileData([]);
                        setShowPreview(false);
                        setMessage(validationErrors.join('\n'));
                        setMessageType('error');
                        return;
                    }

                    // Continuar si no hay errores
                    if (formattedData.length < 2) {
                        setFileData([]);
                        setShowPreview(false);
                        setMessage('El archivo debe contener al menos 2 registros.');
                        setMessageType('error');
                    } else if (formattedData.length > MAX_RECORDS) {
                        setFileData([]);
                        setShowPreview(false);
                        setMessage(`El archivo supera el límite de ${MAX_RECORDS} registros (contiene ${formattedData.length}).`);
                        setMessageType('error');
                    } else {
                        setFileData(formattedData);
                        setShowPreview(true);
                    }
                }
            } catch (error) {
                console.error('Error al procesar el archivo:', error);
                setMessage(`Error: ${error.message || 'Formato de archivo no válido'}`);
                setMessageType('error');
                setShowPreview(false);
            }
        };

        reader.onerror = () => {
            setMessage('Error al leer el archivo.');
            setMessageType('error');
        };

        if (isCSV) {
            reader.readAsText(file, 'UTF-8');
        } else {
            reader.readAsArrayBuffer(file);
        }
    };
    
    const handleFileRestart = () => {
        setSelectedFile(null);
        setFileData([]);
        setShowPreview(false);
        setMessage('');
        setMessageType('');
        setPage(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeSendListeners = () => {
        if (socket) {
            socket.off('sendLinkPlink:progress');
            socket.off('sendLinkPlink:done');
        }
    };

    // Al desmontar la vista removemos los listeners para no acumular duplicados
    useEffect(() => {
        return () => {
            if (socket) {
                socket.off('sendLinkPlink:progress');
                socket.off('sendLinkPlink:done');
            }
        };
    }, [socket]);

    const registerSendListeners = (totalEsperado) => {
        if (!socket) return;
        // Evita listeners duplicados si el usuario hace varios envíos
        removeSendListeners();

        socket.on('sendLinkPlink:progress', (data) => {
            // Payload por lote: { procesados, enviados, fallidos, total }
            setSendProgress(prev => ({
                total: data?.total ?? ((prev && prev.total) || totalEsperado),
                procesados: data?.procesados ?? (prev && prev.procesados) ?? 0,
                enviados: data?.enviados ?? (prev && prev.enviados) ?? 0,
                fallidos: data?.fallidos ?? (prev && prev.fallidos) ?? 0
            }));
        });

        socket.on('sendLinkPlink:done', (data) => {
            removeSendListeners();
            setSendProgress(null);
            setUploading(false);

            const enviados = data?.enviados ?? 0;
            const fallidos = data?.fallidos ?? 0;

            if (data?.exito && fallidos === 0) {
                setMessage(`${enviados} links enviados con éxito`);
                setMessageType('success');
            } else {
                const detalleErrores = (data?.errores || [])
                    .map(err => `• ${err.correo}: ${err.error}`)
                    .join('\n');
                setMessage(
                    `Envío finalizado: ${enviados} links enviados, ${fallidos} fallidos.` +
                    (detalleErrores ? `\n${detalleErrores}` : '')
                );
                setMessageType(data?.exito ? 'success' : 'error');
            }
            setDialogOpen(true);
        });
    };

    const sendMasiveLinks = async (filename) => {
        try {
            // Formateamos los datos para enviar
            const datos = fileData.map(item => ({
                CI: item.CI,
                nombre_del_cliente: item["Nombre del cliente"],
                correo_electrónico: item["Correo Electrónico"],
                numero_de_telefono: item["Numero de Telefono"],
                monto: item.Monto,
                motivo_de_cobro: item["Motivo de cobro"],
            }));

            const data = {
                user: nombre_usuario,
                filename: filename,
                expired: expired,
                campaña: campaña,
                data: datos
            };

            // Registramos los listeners de progreso ANTES de disparar el envío
            // para no perder los primeros eventos del backend
            registerSendListeners(datos.length);

            const response = await axios.post(
                process.env.REACT_APP_URL + process.env.REACT_APP_SENDLINKPLINK,
                data,
               {
                         headers: {
                           'Authorization': `Bearer ${encryptData(token, process.env.REACT_APP_LOGIN_KEY)}`,
                           'id_commerce': encryptData(id_commerce, process.env.REACT_APP_LOGIN_KEY)
                         },
                         // Con hasta 5000 registros el request es pesado; sin timeout explícito
                         // axios esperaría indefinidamente, pero lo acotamos a 5 minutos
                         timeout: 300000
                       }
            );

            if (response.status === 202 && response.data.exito) {
                // El backend responde de inmediato y envía los correos en segundo plano;
                // el progreso llega por Socket.IO (sendLinkPlink:progress / sendLinkPlink:done)
                if (socket) {
                    setSendProgress({
                        procesados: 0,
                        enviados: 0,
                        total: response.data.total || datos.length
                    });
                    setMessage('Enviando correos...');
                    setMessageType('');
                } else {
                    // Sin conexión socket no hay progreso en vivo: informamos y liberamos la UI
                    setUploading(false);
                    setMessage(`Procesando envío de ${response.data.total || datos.length} links. Los correos se están enviando en segundo plano.`);
                    setMessageType('success');
                    setDialogOpen(true);
                }
            } else if (response.data.exito) {
                setMessage('Links generados y enviados con éxito');
                setMessageType('success');
                setDialogOpen(true);
                setUploading(false);
            }
        } catch (error) {
            console.error('Error al enviar los datos:', error);
            removeSendListeners();
            setSendProgress(null);
            setUploading(false);
            setMessage('Error al enviar los datos: ' + (error.response?.data?.message || error.message));
            setMessageType('error');
            setDialogOpen(false);
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setMessage('Por favor, selecciona un archivo primero.');
            setMessageType('error');
            return;
        }

        // Validar que haya al menos 2 registros
        if (fileData.length < 2) {
            setMessage('El archivo debe contener al menos 2 registros.');
            setMessageType('error');
            return;
        }

        // Validar el límite máximo de registros soportado por el backend
        if (fileData.length > MAX_RECORDS) {
            setMessage(`El archivo supera el límite de ${MAX_RECORDS} registros (contiene ${fileData.length}).`);
            setMessageType('error');
            return;
        }

        setUploading(true);
        setMessage('');
        setMessageType('');

        try {
            await sendMasiveLinks(selectedFile.name);
        } catch (error) {
            // El error ya se maneja en sendMasiveLinks; este catch es solo un respaldo
            setUploading(false);
        }
        // Nota: uploading NO se apaga aquí; se mantiene activo hasta recibir
        // "sendLinkPlink:done" por socket (o hasta un error), porque el backend
        // responde 202 de inmediato y sigue enviando correos en segundo plano
    };

    const handleDownload = async (tipo) => {
        try {
            let urlImg = tipo === 1 ? process.env.REACT_APP_XLSX : process.env.REACT_APP_CSV;
            const response = await fetch(urlImg);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = tipo === 1 ? 'plink.xlsx' : 'plink.csv';
            document.body.appendChild(link);
            link.click();

            // Limpieza
            window.URL.revokeObjectURL(url);
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error descargando el archivo:', error);
            setMessage('Error al descargar el formato');
            setMessageType('error');
        }
    };

    // Manejadores de paginación
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Componente para previsualizar el archivo con paginación
    const FilePreview = () => {
        if (!showPreview || fileData.length === 0) return null;

        // Calcular los registros a mostrar
        const startIndex = page * rowsPerPage;
        const endIndex = Math.min(startIndex + rowsPerPage, fileData.length);
        const currentData = fileData.slice(startIndex, endIndex);

        // Obtenemos los encabezados de la primera fila de datos
        const headers = Object.keys(fileData[0]);

        return (
            <div>
                <Typography variant="h6" className={classes.previewTitle}>
                    Vista Previa del Archivo
                </Typography>
                <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table className={classes.table} size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {headers.map((header, index) => (
                                    <TableCell key={index} className={classes.tableHeader}>
                                        {header}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {currentData.map((row, rowIndex) => (
                                <TableRow key={rowIndex}>
                                    {headers.map((header, cellIndex) => (
                                        <TableCell key={cellIndex}>
                                            {row[header] || '-'}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {/* Paginación personalizada */}
                    <TablePagination
                        rowsPerPageOptions={[]}
                        component="div"
                        count={fileData.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={(subProps) => (
                            <TablePaginationActions
                                className={classes.pagination}
                                {...subProps}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        )}
                        labelDisplayedRows={({ from, to, count }) => (
                            <span>
                                {from}-{to} de {count}
                            </span>
                        )}
                    />
                </TableContainer>

                <Box className={classes.confirmSection}>
                    <Typography variant="body1" gutterBottom>
                        <strong>{fileData.length}</strong> registros detectados
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        ¿Desea continuar con el envío de estos datos?
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={handleUpload}
                        disabled={uploading}
                        className={classes.buttonSend}
                        size="large"
                    >
                        {uploading ? (
                            <>
                                <CircularProgress size={24} style={{ marginRight: 10 }} />
                                {sendProgress ? 'Enviando correos...' : 'Procesando...'}
                            </>
                        ) : (
                            'Confirmar y Enviar'
                        )}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleFileRestart}
                        disabled={uploading}
                        className={classes.buttonCancel}
                        size="large"
                    >
                        Cancelar
                    </Button>

                    {sendProgress && (
                        <Box style={{ marginTop: 16 }}>
                            <Typography variant="body2" gutterBottom>
                                Enviando correos... {sendProgress.procesados} / {sendProgress.total}
                            </Typography>
                            <LinearProgress
                                variant="determinate"
                                value={sendProgress.total > 0 ? Math.min(100, (sendProgress.procesados / sendProgress.total) * 100) : 0}
                            />
                        </Box>
                    )}
                </Box>
            </div>
        );
    };

    return (
        <Container maxWidth="false">
            <DialogMessage
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                typeMessage={messageType}
                handleFileRestart={handleFileRestart}
                message={
                    messageType === 'error' && message.includes('al menos 2 registros')
                        ? "Error: El archivo debe contener al menos 2 registros para procesar."
                        : message
                }
                setActiveStep={setActiveStep}
                setExpiredToken={setExpiredToken}
                setTipo={setTipo}
                setCampaña={setCampaña}
            />

            <Typography variant="h6" gutterBottom style={{ fontWeight: 'bold' }}>
                Enviar Links Masivos
            </Typography>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 'lighter' }}>
                Paso 1 : Descargar el formato .XLSX o .CSV
            </Typography>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 'lighter' }}>
                Paso 2 : Completar el formato (Todos los datos son requeridos, máximo 5000 registros por archivo)
            </Typography>
            <Typography variant="h6" gutterBottom style={{ fontWeight: 'lighter' }}>
                Paso 3 : Adjunta el archivo del Paso 2 a la sección de archivos masivos
            </Typography>

            <Paper elevation={0} className={classes.root}>
                <Grid container spacing={2}>
                    <Grid container item xs={12} sm={10} md={8} spacing={2} justify="center">
                        <Grid item xs={12} sm={4}>
                            <Typography
                                variant="h5"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                }}
                                gutterBottom
                            >
                                Descargar Formato
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography
                                variant="h5"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    cursor: 'pointer'
                                }}
                                gutterBottom
                                onClick={() => handleDownload(2)}
                            >
                                .CSV
                                <GetAppRoundedIcon
                                    style={{ marginLeft: '8px' }}
                                    fontSize="large"
                                />
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sm={4}>
                            <Typography
                                variant="h5"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    cursor: 'pointer'
                                }}
                                gutterBottom
                                onClick={() => handleDownload(1)}
                            >
                                .XLSX
                                <GetAppRoundedIcon
                                    style={{ marginLeft: '8px' }}
                                    fontSize="large"
                                />
                            </Typography>
                        </Grid>
                    </Grid>
                </Grid>
            </Paper>

            <Paper elevation={0} className={classes.root}>
                <Grid container spacing={2} justify="center" alignItems="left">
                    <Grid container item xs={12} sm={12} md={12} spacing={2} justify="left">
                        <Grid item xs={12}>
                            <input
                                accept=".xlsx,.xls,.csv"
                                className={classes.input}
                                id="contained-button-file"
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                            <label htmlFor="contained-button-file">
                                <Button
                                    color="default"
                                    component="span"
                                    className={classes.button}
                                    endIcon={<CloudUploadIcon />}
                                >
                                    Sección de Links Masivos
                                </Button>
                            </label>

                            {selectedFile && (
                                <>
                                    <Typography variant="subtitle1" className={classes.fileName}>
                                        Archivo seleccionado: <strong>{selectedFile.name}</strong>
                                        <Button
                                            variant='outlined'
                                            className={classes.restartFileButton}
                                            onClick={handleFileRestart}
                                        >
                                            Borrar
                                        </Button>
                                    </Typography>
                                    <FilePreview setActiveStep={setActiveStep} />
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                {message && (
                    <Box className={classes.statusMessage}>
                        <Typography
                            variant="body1"
                            className={
                                messageType === 'error' ? classes.errorText :
                                    (messageType === 'success' ? classes.successText : '')
                            }
                        >
                            {message}
                        </Typography>
                    </Box>
                )}
            </Paper>
        </Container>
    );
}

export default MasiveLinks;